import { useState, useEffect, useRef } from 'react'
import { useLoadScript } from '@react-google-maps/api'
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Port } from '../data/ports'
import { Boat, Airplane, MapPin, MagnifyingGlass, Spinner, ArrowsClockwise, X } from '@phosphor-icons/react'

const libraries: ("places")[] = ["places"]
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000'

interface PortSelectorProps {
  label: string
  type?: 'seaport' | 'airport' | 'inland'
  value?: {
    city: string
    state?: string
    country: string
    portCode?: string
    portName?: string
    zip_code?: string
  }
  onChange: (port: {
    city: string
    state?: string
    country: string
    portCode?: string
    portName?: string
    zip_code?: string
  }) => void
  placeholder?: string
  required?: boolean
  error?: string
}

// Inner component that handles the autocomplete logic
function PortSelectorInner({
  label,
  type,
  value,
  onChange,
  placeholder = 'Busque por porto, cidade ou país...',
  required = false,
  error
}: PortSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [results, setResults] = useState<Port[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedPort, setSelectedPort] = useState<Port | null>(null)
  const [useGoogleFallback, setUseGoogleFallback] = useState(false)
  const [selectedGoogleAddress, setSelectedGoogleAddress] = useState<any>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Google Places autocomplete
  const {
    ready: googleReady,
    value: googleInputValue,
    suggestions: { status: googleStatus, data: googleData },
    setValue: setGoogleInputValue,
    clearSuggestions: clearGoogleSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: type === 'seaport' ? ['establishment'] : type === 'airport' ? ['airport'] : ['geocode'],
    },
    debounce: 300,
  })

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        clearGoogleSuggestions()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [clearGoogleSuggestions])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [])

  // Search locations from API
  const searchLocations = async (query: string) => {
    if (query.length < 2) {
      setResults([])
      return
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    try {
      const params = new URLSearchParams({ q: query, limit: '15' })
      if (type) params.set('type', type)

      const response = await fetch(
        `${API_BASE}/locations/search?${params}`,
        { signal: abortControllerRef.current.signal }
      )
      const data = await response.json()
      const locations: Port[] = (data.results || []).map((loc: any) => ({
        code: loc.code,
        name: loc.name,
        city: loc.city,
        state: loc.subdivision,
        country: loc.country,
        countryCode: loc.countryCode,
        type: loc.type,
        coordinates: loc.coordinates,
      }))
      setResults(locations)
      setIsOpen(true)
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Location search error:', error)
        setResults([])
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Set initial search term from value
  useEffect(() => {
    if (value && value.portName && value.portCode) {
      const mockPort: Port = {
        code: value.portCode,
        name: value.portName,
        city: value.city,
        state: value.state,
        country: value.country,
        countryCode: (value as any).countryCode || '',
        type: type || 'seaport'
      }
      setSelectedPort(mockPort)
      setSearchTerm(`${value.portName} - ${value.city}, ${value.country}`)
    } else if (value && value.city) {
      // Could be a Google Places address
      const displayValue = [value.city, value.state, value.country].filter(Boolean).join(', ')
      setSearchTerm(displayValue)
      if (!value.portCode) {
        setSelectedGoogleAddress(value)
      }
    }
  }, [value, type])

  const handleSelectPort = (port: Port) => {
    setSelectedPort(port)
    setSearchTerm(`${port.name} - ${port.city}, ${port.country}`)
    setIsOpen(false)
    setResults([])
    setUseGoogleFallback(false)
    onChange({
      city: port.city,
      state: port.state,
      country: port.country,
      portCode: port.code,
      portName: port.name
    })
  }

  const handleSelectGooglePlace = async (description: string, placeId: string) => {
    setGoogleInputValue(description, false)
    clearGoogleSuggestions()
    setIsOpen(false)

    try {
      const results = await getGeocode({ address: description })
      const addressComponents = results[0].address_components

      const addressData: any = {
        city: '',
        country: '',
      }

      // Collect candidates by type so we can fall back when the ideal one
      // (locality / municipality) is missing — otherwise city stays empty and
      // the wizard's "Próximo" button never enables.
      const byType: Record<string, string> = {}
      addressComponents.forEach((component: any) => {
        component.types.forEach((t: string) => { byType[t] = component.long_name })
        if (component.types.includes('administrative_area_level_1')) {
          addressData.state = component.short_name
        }
        if (component.types.includes('country')) {
          addressData.country = component.long_name
        }
        if (component.types.includes('postal_code')) {
          addressData.zip_code = component.long_name
        }
      })

      addressData.city =
        byType['locality'] ||
        byType['administrative_area_level_2'] ||
        byType['postal_town'] ||
        byType['sublocality'] ||
        byType['administrative_area_level_1'] ||
        // last resort: first meaningful chunk of the chosen suggestion
        description.split(',')[0].trim()

      // Use place name as port name for display
      addressData.portName = description.split(',')[0]

      setSelectedGoogleAddress(addressData)
      setSearchTerm(description)
      setUseGoogleFallback(false)
      onChange(addressData)
    } catch (error) {
      console.error('Error getting address details:', error)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)

    if (selectedPort) {
      setSelectedPort(null)
    }
    if (selectedGoogleAddress) {
      setSelectedGoogleAddress(null)
    }

    if (useGoogleFallback) {
      setGoogleInputValue(value)
    } else {
      // Debounced API search
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        searchLocations(value)
      }, 300)
    }
  }

  const handleSwitchToGoogle = () => {
    setUseGoogleFallback(true)
    setResults([])
    setGoogleInputValue(searchTerm)
  }

  const handleSwitchToPorts = () => {
    setUseGoogleFallback(false)
    clearGoogleSuggestions()
    if (searchTerm.length >= 2) {
      searchLocations(searchTerm)
    }
  }

  const handleClear = () => {
    setSearchTerm('')
    setSelectedPort(null)
    setSelectedGoogleAddress(null)
    setResults([])
    setUseGoogleFallback(false)
    clearGoogleSuggestions()
  }

  const getPortIcon = (portType: string) => {
    switch (portType) {
      case 'seaport':
        return <Boat size={18} weight="duotone" className="text-blue-600" />
      case 'airport':
        return <Airplane size={18} weight="duotone" className="text-purple-600" />
      default:
        return <MapPin size={18} weight="duotone" className="text-green-600" />
    }
  }

  const getPortTypeLabel = (portType: string) => {
    switch (portType) {
      case 'seaport':
        return 'Porto Marítimo'
      case 'airport':
        return 'Aeroporto'
      default:
        return 'Terminal'
    }
  }

  const showPortResults = !useGoogleFallback && isOpen && results.length > 0 && !selectedPort && !selectedGoogleAddress
  const showNoPortResults = !useGoogleFallback && isOpen && searchTerm.length >= 2 && results.length === 0 && !isLoading && !selectedPort && !selectedGoogleAddress
  const showGoogleResults = useGoogleFallback && googleStatus === 'OK' && googleData.length > 0 && !selectedGoogleAddress

  return (
    <div ref={wrapperRef} className="relative">
      <Label>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div className="relative mt-1">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          {isLoading ? (
            <Spinner size={18} className="animate-spin" />
          ) : (
            <MagnifyingGlass size={18} />
          )}
        </div>
        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => {
            if (useGoogleFallback && googleData.length > 0) {
              setIsOpen(true)
            } else if (results.length > 0 && !selectedPort) {
              setIsOpen(true)
            }
          }}
          placeholder={useGoogleFallback ? 'Buscar endereço no Google...' : placeholder}
          className="pl-10 pr-10"
        />

        {(searchTerm || selectedPort || selectedGoogleAddress) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Mode indicator */}
      {useGoogleFallback && (
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-amber-600 flex items-center gap-1">
            <MapPin size={12} />
            Buscando via Google Maps
          </span>
          <button
            type="button"
            onClick={handleSwitchToPorts}
            className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
          >
            <ArrowsClockwise size={12} />
            Voltar para portos
          </button>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}

      {/* Port Results */}
      {showPortResults && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
          <div className="p-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
              {results.length} resultado{results.length > 1 ? 's' : ''} encontrado{results.length > 1 ? 's' : ''}
            </p>
            {results.map((port) => (
              <button
                key={port.code}
                type="button"
                onClick={() => handleSelectPort(port)}
                className="w-full flex items-start gap-3 px-3 py-3 hover:bg-emerald-50 rounded-lg transition-colors text-left group"
              >
                <div className="mt-0.5 flex-shrink-0">
                  {getPortIcon(port.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-sm text-gray-900 truncate group-hover:text-emerald-700">
                      {port.name}
                    </p>
                    <span className="px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-600 flex-shrink-0">
                      {port.code}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span>{port.city}</span>
                    {port.state && (
                      <>
                        <span>•</span>
                        <span>{port.state}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>{port.country}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{getPortTypeLabel(port.type)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No Port Results - Show Google Fallback Option */}
      {showNoPortResults && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl z-50 p-4">
          <div className="text-center mb-4">
            <MagnifyingGlass size={32} className="mx-auto text-gray-300 mb-2" weight="duotone" />
            <p className="text-sm text-gray-500">
              Nenhum {type === 'seaport' ? 'porto' : type === 'airport' ? 'aeroporto' : 'local'} encontrado para "{searchTerm}"
            </p>
          </div>

          <button
            type="button"
            onClick={handleSwitchToGoogle}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg transition-colors border border-amber-200"
          >
            <MapPin size={18} weight="duotone" />
            <span className="font-medium">Buscar outro endereço via Google Maps</span>
          </button>

          <p className="text-xs text-gray-400 mt-2 text-center">
            Use esta opção para endereços que não estão na lista de portos/aeroportos
          </p>
        </div>
      )}

      {/* Google Places Results */}
      {showGoogleResults && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-amber-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
          <div className="p-2">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide px-3 py-2 flex items-center gap-1">
              <MapPin size={12} />
              Resultados do Google Maps
            </p>
            {googleData.map((suggestion) => (
              <button
                key={suggestion.place_id}
                type="button"
                onClick={() => handleSelectGooglePlace(suggestion.description, suggestion.place_id)}
                className="w-full flex items-start gap-3 px-3 py-3 hover:bg-amber-50 rounded-lg transition-colors text-left group"
              >
                <div className="mt-0.5 flex-shrink-0">
                  <MapPin size={18} weight="duotone" className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900 truncate group-hover:text-amber-700">
                    {suggestion.structured_formatting.main_text}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {suggestion.structured_formatting.secondary_text}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Helper text */}
      {!error && !useGoogleFallback && (
        <p className="text-xs text-gray-500 mt-1">
          {type === 'seaport' && 'Busque por portos marítimos (ex: Santos, Rotterdam, Shanghai)'}
          {type === 'airport' && 'Busque por aeroportos (ex: GRU, JFK, Frankfurt)'}
          {!type && 'Busque por portos, aeroportos ou cidades'}
        </p>
      )}
    </div>
  )
}

// Wrapper component that loads the Google Maps script
export function PortSelector(props: PortSelectorProps) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries,
  })

  // Even if Google fails, we can still use the port search
  if (loadError) {
    console.warn('Google Maps failed to load, using port search only')
  }

  if (!isLoaded) {
    return (
      <div className="relative">
        <Label>
          {props.label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <div className="relative mt-1">
          <Spinner size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
          <Input
            type="text"
            disabled
            placeholder="Carregando..."
            className="pl-10"
          />
        </div>
      </div>
    )
  }

  return <PortSelectorInner {...props} />
}
